import type { Direction } from '@mui/material/styles';

import { useEffect } from 'react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import RtlProvider from '@mui/system/RtlProvider';

// ----------------------------------------------------------------------

type RtlProps = {
  direction: Direction;
  children: React.ReactNode;
};

// Emotion's default cache key, so ltr class names stay exactly what they were without a provider.
const cacheLtr = createCache({ key: 'css' });

const cacheRtl = createCache({
  key: 'rtl',
  prepend: true,
  stylisPlugins: [rtlPlugin],
});

export function Rtl({ children, direction }: RtlProps) {
  useEffect(() => {
    document.dir = direction;
  }, [direction]);

  // One provider for both directions keeps the tree shape, so switching language does not remount the page.
  return (
    <CacheProvider value={direction === 'rtl' ? cacheRtl : cacheLtr}>{children}</CacheProvider>
  );
}

// For content that reads left to right in every language, like media transport controls.
// Styles skip the rtl flip and MUI components run in their ltr mode (a Slider no longer
// reverses its axis). The children still need dir="ltr" for their own layout order.
export function Ltr({ children }: { children: React.ReactNode }) {
  return (
    <CacheProvider value={cacheLtr}>
      <RtlProvider value={false}>{children}</RtlProvider>
    </CacheProvider>
  );
}
